import type { Merge } from 'type-fest'

import type { NoteGenerationMode, UserRole } from './auth-interfaces'
import type { IHistoryItem } from './ui-interfaces'

export interface IPaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number
  to: number
}

export interface IDataWithMeta<T> {
  data: T
  meta: IPaginationMeta
}

// export interface RequestDataError {
//   message: string
//   errors: Record<string, string[]>
// }

export interface IVitalSign {
  created_at: string
  id: string
  name: string
  patient_id: string
  unit: string
  updated_at: Date | string
  value: string
}

export interface IPatient {
  first_name: string
  last_name: string
  gender?: 'male' | 'female' | 'unspecified'
  vital_signs: IVitalSign[]
  id: string
  dob: string
  age: string
  medical_record_number?: string
  phone?: string
  email?: string
}

export interface ITranscriptContent {
  startTimeFormatted: string
  content: string
  startTime: number
}

export interface ITranscript {
  created_at: Date | string
  encounter_id: string
  id: string
  transcript: string
  parsedTranscript: ITranscriptContent[]
  original: string
  parsedOriginal: ITranscriptContent[]
  updated_at: Date | string
  user_id: string
  rating?: number
  history: ITranscriptHistory
  language?: 'en' | 'es'
}

export type ITranscriptHistory = Record<
  string,
  {
    name: string
    history: IHistoryItem[]
  }
>

export interface INote {
  merged_at?: Date | string
  created_at: Date | string
  encounter_id: string
  id: string
  // status: EncounterNoteStatuses
  updated_at: Date | string
  user_id: string
  rating?: number
  data: string | null
  original_data: string | null
  removed_data: string | null
  note_type: INoteType
  note_type_id: INoteType['id']
  parsedData: INoteDataItem[]
  parsedOriginalData: INoteDataItem[]
}

export interface IMergeHPIRequest {
  note_id: INote['id'] | string
  content: string
}

export interface IHPIData {
  section_name: string
  content: string
  created_at: Date | string
  note_id: INote['id'] | string
  note_type: string
  provider_title: string
  appointment_type?: string
}

export interface INoteType {
  id: number
  name: string
  is_active: boolean
  visit_type: 'first visit' | 'follow up' | 'clinic' | 'inpatient' | 'office'
  speciality:
    | 'general'
    | 'neurology'
    | 'internal medicine'
    | 'pain management'
    | 'rheumatology'
    | 'functional medicine'
    | 'orthopedics'
  is_shared: boolean
  is_default: boolean
  note_tune: boolean
  components: INoteTypeComponent[]
  is_attestation_text_enabled: boolean
  attestation_text?: string
  is_recording_time_enabled: boolean
  recording_time_text?: string
  recording_time_format?: string
}

export type INoteTypeRes = Merge<
  INoteType,
  {
    is_active: 0 | 1
    is_shared: 0 | 1
    is_default: 0 | 1
    note_tune: 0 | 1
  }
>

export interface INoteTypeComponent {
  id: number
  note_type_id: INoteType['id']
  name: string
  format: 'list' | 'narrative' | 'structured template' | 'exam' | 'problem oriented'
  length: 'concise' | 'normal' | 'verbose'
  definition: string
  supposed_to_capture?: string
  special_instructions: string
  template_structure: string
  exam_templates: IExamTemplate[]
  dynamic_fields: IDynamicField[]
  dynamic_fields_mapping: string
  dynamic_fields_processed_at: Date | string
}

export interface INoteTypeComponentReq extends INoteTypeComponent {
  order: number
}

export interface INoteTypeExamComponentReq {
  note_type_id: number
  name: string
  order: number
  exams: number[]
}

export type IEncounter = {
  id: string
  external_id?: string
  patient_id: string
  patient?: IPatient
  provider_id: string
  created_at: Date | string
  updated_at: Date | string
  visit_at: Date | string
  status: EncounterStatuses
  transcript_error: string | null
  note_error: string | null
  note_type_id: INoteType['id'] | null
  duration: number
  recorded: boolean
  language?: 'en' | 'es'
  appointment_type: string
  completed_in_ehr: boolean
  is_checked_in?: boolean
  patient_context?: string
  title?: string
  rating?: number
  encounter_status: EncounterStatusesV2Enum
  note_generation_mode: NoteGenerationMode
} & (
  | {
      transcript_status: 'not_transcribed' | 'transcripting' | 'transcript_error'
      transcript: undefined
      note_status: 'not_generated'
      note: undefined
    }
  | {
      transcript_status: 'transcribed'
      transcript: ITranscript
      note_status: 'not_generated' | 'pending' | 'error'
      note: undefined
    }
  | {
      transcript_status: 'transcribed'
      transcript: ITranscript
      note_status: 'new'
      note: INote
    }
)

export interface IFeedback {
  id?: string
  user_id?: string
  url: string
  message: string
}

export interface IPatientsQuery {
  is_global?: 0 | 1
  search?: string
  page?: number
  limit?: number
  with_encounters?: 0 | 1
}

export interface IAddPatientMutation {
  first_name: string
  last_name: string
  medical_record_number?: string
  gender?: 'male' | 'female' | 'unspecified'
  dob: string
  phone?: string
  email?: string
}

export interface IEditPatientMutation extends IAddPatientMutation {
  id: string
}

export interface INoteDataItem {
  title: string
  content:
    | string
    | null
    | {
        title: string | null
        content: string | null
        hide?: boolean
      }[]
  hide?: boolean
  merged_at?: Date | string
  merged_ids?: string[]
  order?: number
}

export type IBackgroundJob =
  | {
      status: 'pending'
      message: undefined
    }
  | {
      status: 'success' | 'fail'
      message: string
    }

export enum EncounterSteps {
  TRANSCRIPT = 'Transcript',
  NOTE = 'Note',
  // REVIEW = 'Review',
}

export type EncounterStatuses = 'not_started' | 'in_progress' | 'completed'

export enum EncounterStatusesEnum {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export enum EncounterStatusesV2Enum {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  NOTE_READY = 'note_ready',
  CLOSED = 'closed',
  SENT_TO_EHR = 'sent_to_ehr',
}

// export type EncounterNoteStatuses = 'new' | 'in_review'

export enum TranscriptStatuses {
  NOT_STARTED = 'not_transcribed',
  IN_PROGRESS = 'transcripting',
  SUCCESS = 'transcribed',
  ERROR = 'transcript_error',
}

export enum NoteStatuses {
  NOT_STARTED = 'not_generated',
  IN_PROGRESS = 'pending',
  SUCCESS = 'new',
  ERROR = 'error',
}

export interface IExamTemplate {
  id: number
  title: string
  text: string
  type: 'ros' | 'pe'
  is_shared: boolean
}

export type IExamTemplateRes = Merge<IExamTemplate, { is_shared: 0 | 1 }>

export interface IDeepgramKeyResponse {
  key: string
}

export interface ITranscribeFileRequest extends FormData {
  append(name: 'file', value: File, fileName?: string): void
  append(name: 'model' | 'language' | 'mime_type', value: string): void
  append(name: 'override_transcript', value: 'true' | 'false'): void
  append(
    name: 'smart_format' | 'diarize' | 'measurements' | 'numerals',
    value: boolean | string,
  ): void
}

export interface IDeepgramTranscriptionResponse {
  results: {
    channels: {
      alternatives: {
        paragraphs: {
          paragraphs: {
            sentences: {
              text: string
              start: number
              end: number
              confidence: number
            }[]
          }[]
        }
        transcript: string
        words: {
          word: string
          start: number
          end: number
          confidence: number
          punctuated_word: string
        }[]
      }[]
    }[]
  }
}

export interface IGetEncountersParams {
  page?: number
  limit?: number
  visit_at?: IEncounter['visit_at']
  title?: string
  status?: IEncounter['encounter_status'][] | string
  sort?: {
    column: 'interacted_at' | 'started_at' | 'visit_at'
    direction: 'asc' | 'desc'
  }
  patient_name?: string
  ehr_encounter_filter?: boolean
}

export const UserSettings = {
  HPI_MERGING: 'hpi_merging',
  HIDE_RECORDING_PERMISSION_PROMPT: 'hide_recording_permission_prompt',
  DEFAULT_NOTE_GENERATION_MODE: 'default_note_generation_mode',
  DIAGNOSIS_AND_ORDERING: 'diagnosis_and_ordering',
  NOTE_AUTO_SUBMISSION_TO_EHR: 'note_auto_submission_to_ehr',
  DEFAULT_VISIT_FILTER_OPTIONS: 'default_visit_filter_options',
}

export const OrganizationSettings = {
  CROSS_PROVIDER_ACCESS: 'cross_provider_access',
  NOTE_AUTO_SUBMISSION_TO_EHR: 'note_auto_submission_to_ehr',
  SCHEDULING: 'scheduling',
}

export interface IEditOrganizationUserRequest {
  organization_id: string
  user_id: string
  body: {
    roles?: UserRole[]
    email?: string
    first_name?: string
    last_name?: string
  }
}

export interface ICreateOrganizationUserRequest {
  organization_id: string
  body: {
    roles: UserRole[]
    email: string
    first_name: string
    last_name: string
  }
}

export interface ICustomerPortalLink {
  url: string
}

export interface IDynamicField {
  dynamic_field: string
  dynamic_field_context: string
  dynamic_field_instance: string
  dynamic_field_definition: string
}

export interface IKeyword {
  id: string
  keyword: string
  weight: number
  user_id: string
  created_at: Date | string
  updated_at: Date | string
}

export interface IMacro {
  id: string
  is_active: boolean
  note_section_types: ('HPI' | 'ROS' | 'PE' | 'AP')[]
  autofill_text: string
  trigger_phrase: string
}
